import React from 'react';
import { withApollo, Query, Mutation } from 'react-apollo';
import PropTypes from 'prop-types';
import Script from 'react-load-script';
import isUrl from 'is-url';
import log from 'loglevel';

import { updateUserAsset as UPDATE_USER_ASSET } from 'GraphQL/mutation/Classroom.mutation';
// NOTE: ANY-1653 For now, userAsset table does not exist in the database at all, it was implemented from PAAS project. So, if one day we need to use it, we need implement userAsset table first
import { getUserAsset as GET_USER_ASSET } from 'GraphQL/query/Classroom.query';
import { Loading } from 'Components/Base/Icon/SkooldioSVGIcons';
import getConfig from 'Util/Config';

const handleLiveCodeModuleLoaded = (LiveCodeOptions) => {
  let LiveCodeApp;
  if (window.SkooldioLivecoding) {
    LiveCodeApp = window.SkooldioLivecoding.new({
      selector: '#liveCodingApp',
      options: LiveCodeOptions,
    });
  }
  if (LiveCodeApp) {
    LiveCodeApp.render();
  } else {
    log.error('SkooldioLivecoding instance is missing.');
  }
};

const getLiveCodingEndpoint = (courseName) => {
  const {
    liveCodingApi: LIVECODING_API,
    jupyterNotebookLivecodeApi: JUPYTER_LIVECODING_API,
    jupyterCourseNames = [],
  } = getConfig();
  if (jupyterCourseNames.includes(courseName)) {
    return JUPYTER_LIVECODING_API;
  }

  return LIVECODING_API;
};

const LiveCodeApp = ({ moduleUrl, options }) => {
  return (
    <>
      <div id="liveCodingApp" />
      <Script
        url={moduleUrl}
        onLoad={() => {
          handleLiveCodeModuleLoaded(options);
        }}
      />
    </>
  );
};

const GoogleSheetLoading = () => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Loading style={{ fill: '#FAA919' }} />
      <p style={{ marginTop: '16px' }}>กำลังสร้าง Sheet ของคุณ ...</p>
    </div>
  );
};

LiveCodeApp.propTypes = {
  moduleUrl: PropTypes.string,
  options: PropTypes.object,
};

/**
 * @typedef LiveCodeMetadata
 * @property {'PYTHON' | 'R' | 'SQL'} language
 * @property {string} pec Pre-exercise code
 * @property {string} courseName
 */

/**
 * @typedef GoogleSheetMetadata
 * @property {'GOOGLE_SHEET'} language
 * @property {string} spreadsheetId
 * @property {string} sheetId
 */

/**
 * @param {object} props
 * @param {LiveCodeMetadata | GoogleSheetMetadata } props.value
 * @param {() => void} props.onCompleted
 * @param {string} props.assetReferId
 */
const LiveCoding = ({ value, onCompleted, assetReferId }) => {
  const { liveCodingModuleUrl: LIVECODING_MODULE_URL } = getConfig();

  if (!isUrl(LIVECODING_MODULE_URL)) {
    return null;
  }

  if (value.language === 'GOOGLE_SHEET') {
    const { language, spreadsheetId, sheetId } = value;
    return (
      <LiveCodeApp
        moduleUrl={LIVECODING_MODULE_URL}
        options={{
          apiEndpoint: getConfig().liveCodingApi,
          language: language.toLowerCase(),
          spreadsheetId,
          sheetId,
          renderLoading: GoogleSheetLoading,
          onCompleted,
          completeTimeout: getConfig().liveCodingMarkCompletionTimeOut,
        }}
      />
    );
  }

  return (
    <Query
      query={GET_USER_ASSET}
      variables={{
        assetReferId,
      }}
    >
      {({ data, loading, error }) => {
        return (
          <Mutation mutation={UPDATE_USER_ASSET}>
            {(updateUserAsset) => {
              if (error) {
                log.error(error);
              }

              const { liveCodingModuleUrl: LIVECODING_MODULE_URL } = getConfig();
              const { language, courseName, pec, ...options } = value;
              const LiveCodeOptions = {
                apiEndpoint: getLiveCodingEndpoint(courseName),
                onCompleted: onCompleted,
                language: (language ?? '').toLowerCase(),
                preExerciseCode: pec,
                onSubmitted: async ({ userAssetDoc }) => {
                  return updateUserAsset({
                    variables: {
                      assetReferId: assetReferId,
                      document: userAssetDoc,
                    },
                  });
                },
                userAssetCode: data?.userAsset?.document?.userCode ?? null,
                ...options,
              };

              if (loading) {
                // Due to unknown reasons, we cannot return Loading Component in form of JSX
                // Otherwise, it would cause "React Minify Error" on production environment
                // See SKOOLDIO-5610 for details
                return Loading({});
              }

              return <LiveCodeApp moduleUrl={LIVECODING_MODULE_URL} options={LiveCodeOptions} />;
            }}
          </Mutation>
        );
      }}
    </Query>
  );
};

LiveCoding.propTypes = {
  assetReferId: PropTypes.string,
  value: PropTypes.object,
  onCompleted: PropTypes.func,
};

export default withApollo(LiveCoding);
